import React, { useEffect } from "react";
import "../styles/Pantry.css";
import { useIngredients } from "../context/IngredientsProvider";
import ingredients from "../data/ingredients";
import { supabase } from "../supabaseClient";

interface StoredIngredientsProps {
  session: any;
  sortOption: "alphabetical" | "category";
}

const StoredIngredients: React.FC<StoredIngredientsProps> = ({
  session,
  sortOption,
}) => {
  const { selectedIngredients, setSelectedIngredients } = useIngredients();

  useEffect(() => {
    const fetchIngredients = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (user) {
          const { data, error } = await supabase
            .from("user_ingredients")
            .select("label, value")
            .eq("user_id", user.id);

          if (error) {
            console.error("Virhe haettaessa raaka-aineita:", error);
          } else {
            setSelectedIngredients(data || []);
          }
        } else {
          console.log("🔍 Vieraskäyttäjä – ei nollata valittuja raaka-aineita");
        }
      } catch (error) {
        console.error("Virhe haettaessa raaka-aineita:", error);
      }
    };

    fetchIngredients();
  }, [session, setSelectedIngredients]);

  const removeIngredient = async (value: string) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        const { error } = await supabase
          .from("user_ingredients")
          .delete()
          .eq("value", value)
          .eq("user_id", user.id);

        if (error) {
          console.error("Virhe poistettaessa raaka-ainetta:", error);
        }
      }

      setSelectedIngredients((prev) =>
        prev.filter((ing) => ing.value !== value)
      );
    } catch (error) {
      console.error("Virhe poistettaessa raaka-ainetta:", error);
    }
  };

  // 🔧 Kategorian haku `name`-kentästä (ingredients.ts)
  const getCategory = (value: string) =>
    ingredients.find((ing) => ing.name.toLowerCase() === value.toLowerCase())
      ?.category || "Muu";

  const getGroup = (label: string) => {
    const l = label[0].toUpperCase();
    if (/[A-H]/.test(l)) return "A-H";
    if (/[I-M]/.test(l)) return "I-M";
    if (/[N-S]/.test(l)) return "N-S";
    return "T-Ö";
  };

  const sortedIngredients = [...selectedIngredients].sort((a, b) => {
    const catA = getCategory(a.value);
    const catB = getCategory(b.value);
    return sortOption === "alphabetical"
      ? a.label.localeCompare(b.label)
      : catA.localeCompare(catB) || a.label.localeCompare(b.label);
  });

  const grouped = sortedIngredients.reduce((acc, ing) => {
    const key =
      sortOption === "alphabetical"
        ? getGroup(ing.label)
        : getCategory(ing.value);
    acc[key] = acc[key] || [];
    acc[key].push(ing);
    return acc;
  }, {} as Record<string, typeof sortedIngredients>);

  return (
    <div className="selected-ingredients">
      {sortedIngredients.length > 0 ? (
        Object.entries(grouped).map(([group, items]) => (
          <div key={group} className="ingredient-category">
            <h3 className="category-title">{group}</h3>
            <div className="ingredient-list">
              {items.map((ingredient) => (
                <span
                  key={ingredient.value}
                  className={`ingredient-tag ${getCategory(ingredient.value)
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                >
                  {ingredient.label}
                  <button
                    className="remove-ingredient"
                    onClick={() => removeIngredient(ingredient.value)}
                  >
                    X
                  </button>
                </span>
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="empty-pantry-wrapper">
          <p className="empty-pantry-overlay"></p>
        </div>
      )}
    </div>
  );
};

export default StoredIngredients;
